import { useDriverOnboarding } from '@/hooks/queries/onboarding/useDriverOnboarding'
import { useDriverOnboardingStream } from '@/hooks/useDriverOnboardingStream'
import { performTenantSwitch } from '@/lib/tenantSwitch'
import { getTenant, setIsSwitchingTenant } from '@/stores/session'
import { createFileRoute, Outlet, redirect, useParams } from '@tanstack/react-router'

// Carries no UI — it exists to be keyed. Mounting the stream directly on
// `TenantScopedOutlet` would bind it to whichever carrier was first: that
// component deliberately survives a slug change (see below), so the effect
// would never re-run and the connection would keep a session token that
// `switch-tenant` has already deleted.
function DriverOnboardingStream() {
  // Shares the cache entry with the pages below, and polls on none of its own:
  // this observer only reads whether there is a driver to subscribe to, and
  // most routes under here never mount the query otherwise — giving it the
  // page-level 10s interval would put every screen on that poll to compute a
  // boolean. The drivers row is minted lazily during onboarding and the stream
  // endpoint 404s without one.
  const { data } = useDriverOnboarding({ poll: false })
  useDriverOnboardingStream({ enabled: !!data?.driver })
  return null
}

function TenantScopedOutlet() {
  const { slug } = useParams({ from: '/_auth/carriers/$slug' })

  // Remount the tenant-scoped subtree on every slug change so that all React
  // Query subscriptions reset and refetch with the new session token. Without
  // this, navigating between two tenants on the same route path (e.g.
  // /carriers/A/.../dashboard → /carriers/B/.../dashboard) keeps the same
  // component instance, and observers don't re-run their queryFn even after
  // removeQueries.
  return (
    <>
      <DriverOnboardingStream key={slug} />
      <Outlet key={slug} />
    </>
  )
}

export const Route = createFileRoute('/_auth/carriers/$slug')({
  component: TenantScopedOutlet,
  beforeLoad: async ({ params, context }) => {
    const { slug } = params
    const currentTenant = getTenant()

    if (currentTenant && currentTenant.slug === slug) return

    const fallbackRedirect = () => {
      if (currentTenant?.slug) {
        return redirect({
          to: '/carriers/$slug/driver-onboarding/dashboard',
          params: { slug: currentTenant.slug },
        })
      }
      return redirect({ to: '/login' })
    }

    const meData = context.queryClient.getQueryData<{
      tenants: Array<{ id: string; name: string; slug: string }>
    }>(['me'])

    const targetTenant = meData?.tenants?.find((t) => t.slug === slug)
    if (!targetTenant) throw fallbackRedirect()

    setIsSwitchingTenant(true)
    try {
      await performTenantSwitch(targetTenant.id, context.queryClient)
    } catch {
      throw fallbackRedirect()
    } finally {
      setIsSwitchingTenant(false)
    }
  },
})
